import { Image } from '@mantine/core';
import { useCompanyConfiguration } from 'init/use-company-configuration';
import { i18nKeys, type TranslateFunction, tKeysLoose, useTranslation } from 'locales';

import { IconName } from '../Icon';

import { isEnabledIntegration, useGetIntegrationRights } from './components/Integration.utils';
import {
  Connector,
  Integration,
  type IntegrationCard,
  IntegrationCategory,
} from './Integrations.constants';
import ChiftLogo from './images/chift.png';
import CompanyWebLogo from './images/companyweb.png';
import DBasicsLogo from './images/dbasics.png';

export const isAccountingIntegration = (integration: Integration): boolean => {
  switch (integration) {
    case Integration.YUKI:
    case Integration.HORUS:
    case Integration.TEAMLEADER:
    case Integration.EXACT:
    case Integration.DBASICS:
    case Integration.CHIFT:
      return true;
    default:
      return false;
  }
};

export function useGetIntegrationCard(): (integration: Integration) => IntegrationCard {
  const { t } = useTranslation();
  const getIntegrationRightsFn = useGetIntegrationRights();

  return (integration: Integration) => {
    const integrationRights = getIntegrationRightsFn(integration);
    const base = {
      title: integration,
      icon: IconName[integration.toUpperCase() as keyof typeof IconName],
      // @ts-expect-error
      description: t(tKeysLoose.SETTINGS.INTEGRATIONS[integration.toUpperCase()].DESCRIPTION),
      ...integrationRights,
    };

    switch (integration) {
      case Integration.TEAMLEADER:
        return { ...base, category: IntegrationCategory.ERP };
      case Integration.DIGITEAL:
        return { ...base, icon: IconName.CREDIT_CARD, category: IntegrationCategory.BANKING };
      case Integration.CODABOX:
      case Integration.PONTO:
        return { ...base, category: IntegrationCategory.BANKING };
      case Integration.COMPANYWEB:
        return {
          ...base,
          icon: <Image src={CompanyWebLogo} style={{ filter: 'grayscale(1)' }} />,
          category: IntegrationCategory.MONITORING,
        };
      case Integration.CHIFT:
        return {
          ...base,
          icon: <Image src={ChiftLogo} style={{ filter: 'grayscale(1)' }} />,
          category: IntegrationCategory.ACCOUNTING,
        };
      case Integration.DBASICS:
        return {
          ...base,
          icon: <Image src={DBasicsLogo} style={{ filter: 'grayscale(1)' }} />,
          category: IntegrationCategory.ACCOUNTING,
        };
      default:
        return { ...base, category: IntegrationCategory.ACCOUNTING };
    }
  };
}

export function useGetIntegrationCards() {
  const getIntegrationCardFn = useGetIntegrationCard();

  return Object.values(Integration)
    .map(key => getIntegrationCardFn(key))
    .filter(({ title }) => isEnabledIntegration(title));
}

export function useHasActiveAccountingIntegration(): boolean {
  const company = useCompanyConfiguration();
  const integrationCards = useGetIntegrationCards();

  if (company == null) return false;

  const hasActiveConnector = Object.values(Connector).some(connector =>
    company.tp_accounting.integration_in_use.includes(connector),
  );

  const hasActiveAccountingIntegration = integrationCards.some(
    card =>
      isAccountingIntegration(card.title) && card.isActive && company.tp_accounting.use_import,
  );

  return hasActiveConnector || hasActiveAccountingIntegration;
}

export const getIntegrationsCategories = (t: TranslateFunction) => [
  {
    text: t(i18nKeys.SETTINGS.INTEGRATIONS.ALL),
    category: IntegrationCategory.ALL,
  },
  {
    text: t(i18nKeys.SETTINGS.INTEGRATIONS.ACCOUNTING),
    category: IntegrationCategory.ACCOUNTING,
  },
  {
    text: t(i18nKeys.SETTINGS.INTEGRATIONS.ERP),
    category: IntegrationCategory.ERP,
  },
  {
    text: t(i18nKeys.SETTINGS.INTEGRATIONS.BANKING),
    category: IntegrationCategory.BANKING,
  },
  {
    text: t(i18nKeys.DEBTOR.MONITORING.TAB_TITLE),
    category: IntegrationCategory.MONITORING,
  },
  {
    text: t(i18nKeys.SETTINGS.INTEGRATIONS.MY_INTEGRATIONS),
    category: IntegrationCategory.MY_INTEGRATIONS,
  },
];
